import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'
import { isEqual, addDays } from 'date-fns'
import { toast } from 'react-toastify'
import { useDispatch } from 'react-redux'
import {
  Button,
  Typography,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Box,
  Step,
  Stepper,
  StepLabel,
  Checkbox,
} from '@mui/material'

import { get } from 'utils/lodash'
import { handleError } from 'utils/error'
import { formatDateToBEFormatDateFns, createDateFromString } from 'utils/date'
import { usePostInvoiceBulkPreviewMutation } from 'api/billing/postBulkPreview'
import { useLazyGetAccountBillingPeriodsQuery } from 'api/accounts/getAccountBillingPeriods'
import { usePostBillingCreateInvoicesMutation } from 'api/billing/postBillingCreateInvoices'
import { setCreateInvoicesPreviewData } from 'slices/billing/createInvoicesSlice'
import { getBillingPeriods } from 'data/billing-period/billingPeriodSelectors'
import { isRentalFeeFeatureAvailable, isLateFeeFeatureAvailable } from 'data/launch-darkly/featureSelectors'
import { useLazyGetAccountSettingsQuery } from 'api/accounts/accountSettingsCrud'
import { useLazyGetRentalFeesLinkedToAccountQuery } from 'api/pricing/rentalFeesCrud'
import { isOnRequestService } from 'utils/service'
import { useAccountingLockValue } from 'providers/AccountingLockProvider'

import T from 'T'
import HHTextField from 'components/form-fields/v5/HHTextField'
import SelectItem from 'components/form-fields/v5/SelectItem'
import CancelButton from 'components/buttons/CancelButton'
import Loader from 'components/common/loader'
import HHDesktopDatePicker from 'components/form-fields/v5/HHDesktopDatePicker'

import capitalize from 'lodash/capitalize'
import HHDialogActions from 'components/common/HHDialogActions'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { INVOICE_TYPE } from 'settings/constants/billing'
import PreviewCharges from 'components/customer-details/content/PreviewCharges'
import { useFetchChargeList } from 'components/billing/create-invoices/PreviewChargeListDrawer/useFetchChargeList'
import { mapBillingPeriods, getIsSelectedPeriodDateValidForLateFees } from 'components/customer-details/content/invoices/settings'
import BillingPeriodOptionTooltip from 'components/pricing/LateFees/BillingPeriodOptionTooltip'

const FIRST_STEP_INDEX = 0

const STEPS = [
  { stepName: 'Select an invoice type', optionalText: 'Manual or recurring' },
  { stepName: 'Review', optionalText: 'Make sure your services\nand charges look good' },
]

const { RECURRING, MANUAL } = INVOICE_TYPE

const CreateInvoice = ({ isOpen = false, accountId, billingProfileDetails = {}, callback = noop, onClose }) => {
  const { checkIsLocked, lockDateFormatted } = useAccountingLockValue()
  const dispatch = useDispatch()
  const { onClose: onCloseFetchChargeList, loadMoreRows, isLoadingFirstBatch } = useFetchChargeList()
  const [getAccountBillingPeriods, { data: billingPeriods, isFetching: isLoading, isError }] = useLazyGetAccountBillingPeriodsQuery()
  const [postBillingCreateInvoices, { isLoading: isCreateInvoiceLoading }] = usePostBillingCreateInvoicesMutation()
  const [postInvoiceBulkPreviewMutation, { isLoading: isPreviewInvoiceLoading }] = usePostInvoiceBulkPreviewMutation()
  const [includeLateFees, setIncludeLateFees] = useState(true)
  const [getAccountSettings, { isFetching: isAccountSettingsLoading, data: accountSettingsData }] = useLazyGetAccountSettingsQuery()
  const [getRentalFeesLinkedToAccount, { isFetching: isRentalFeesLoading, data: rentalFeesData = [] }] =
    useLazyGetRentalFeesLinkedToAccountQuery()
  const flags = useFlags()
  const isRentalFeeEnabled = isRentalFeeFeatureAvailable({ flags })
  const onRequestRentalManually = useMemo(() => !get(accountSettingsData, 'onRequestRentalRecurring'), [accountSettingsData])
  const hasOnRequestServiceWithRentalFees = useMemo(
    () => rentalFeesData.find(fee => isOnRequestService(fee?.serviceType)),
    [rentalFeesData]
  )

  const allBillingPeriods = getBillingPeriods({
    billingProfile: billingProfileDetails,
    billingPeriods: isError ? [] : get(billingPeriods, 'accountInvoiceBillingPeriods', []).filter(invoice => !invoice.invoiceId),
  })
  const isLateFeeEnabled = isLateFeeFeatureAvailable({ flags })
  const [activeStep, setActiveStep] = useState(FIRST_STEP_INDEX)
  const [invoiceDate, setInvoiceDate] = useState(new Date())
  const [selectedPeriod, setSelectedPeriod] = useState({ locked: false, invoiceDate: '', periodName: '', startDate: '' })
  const [type, setType] = useState(RECURRING)
  const [resetRentalFees, setResetRentalFees] = useState(true)
  const isRecurringInvoice = type === RECURRING
  const isManualInvoice = type === MANUAL
  const isFirstStep = activeStep === FIRST_STEP_INDEX
  const nextButtonLabel = !isFirstStep ? T.CREATE : T.NEXT
  const selectedPeriodDate = selectedPeriod.invoiceDate
  const finalDate = isEqual(invoiceDate, createDateFromString(selectedPeriodDate))
    ? selectedPeriodDate
    : formatDateToBEFormatDateFns(invoiceDate)
  const isSubmitBtnDisabled = (!selectedPeriodDate && isRecurringInvoice) || !invoiceDate
  const showResetRentalFeesOption = isRentalFeeEnabled && isManualInvoice && onRequestRentalManually && hasOnRequestServiceWithRentalFees
  const mappedBillingPeriods = useMemo(
    () => mapBillingPeriods(allBillingPeriods, includeLateFees, isLateFeeEnabled),
    [allBillingPeriods, includeLateFees, isLateFeeEnabled]
  )
  const isSelectedPeriodValidForLateFees = useMemo(
    () => getIsSelectedPeriodDateValidForLateFees(selectedPeriodDate, mappedBillingPeriods),
    [selectedPeriodDate, mappedBillingPeriods]
  )
  // Invoice date picker should always be visible for manual invoices, and for recurring invoices it only appears when a billing period is selected.
  const showInvoiceDatePicker = isManualInvoice || (isRecurringInvoice && selectedPeriodDate)

  const handleInvoiceTypeChange = e => setType(e.target.value)
  const handleOpenChargesDrawer = async () => {
    postInvoiceBulkPreviewMutation({
      accountIds: [accountId],
      billingPeriodStartDate: selectedPeriod.startDate,
      invoiceType: type,
      invoiceDate: finalDate,
      includeLateFees,
      ...(showResetRentalFeesOption && { resetRentalFees }),
    })
      .unwrap()
      .then(data => {
        dispatch(setCreateInvoicesPreviewData({ ...data, remoteRowCount: 1, prefilledDocIdList: [accountId] }))
        setActiveStep(prevActiveStep => prevActiveStep + 1)
      })
      .catch(handleError)
  }

  const handleIncludeLateFeesChange = e => {
    setIncludeLateFees(e.target.checked)
  }

  const handleCreateInvoice = async () => {
    const payload = {
      ...(isRecurringInvoice && { billingProfileId: get(billingProfileDetails, 'id'), invoicePeriodStartDate: selectedPeriodDate }),
      accountIds: [accountId],
      invoiceDate: finalDate,
      invoiceType: type,
      includeLateFees,
      ...(showResetRentalFeesOption && { resetRentalFees }),
    }
    postBillingCreateInvoices(payload)
      .unwrap()
      .then(() => {
        onClose()
        toast.success(T.INVOICE_IS_GENERATING, {
          hideProgressBar: false,
          autoClose: 4000,
        })
        callback()
      })
      .catch(handleError)
  }

  const handleNext = () => {
    if (isFirstStep) {
      handleOpenChargesDrawer()
      return
    }
    handleCreateInvoice()
  }

  const handleBack = () => {
    if (!isFirstStep) {
      onCloseFetchChargeList()
    }
    setActiveStep(prevActiveStep => prevActiveStep - 1)
  }

  const handleReset = () => {
    setActiveStep(FIRST_STEP_INDEX)
    onCloseFetchChargeList()
    onClose()
  }

  const handleBillingPeriodChange = event => {
    const { value } = event.target
    const periodDetails = get(billingPeriods, 'accountInvoiceBillingPeriods', []).find(bp => bp.invoiceDate === value) || {}
    const periodName = allBillingPeriods.find(bp => bp.invoiceDate === value) || {}
    const locked = get(periodDetails, 'locked')
    setSelectedPeriod({ locked, invoiceDate: value, periodName: get(periodName, 'labelLeft'), startDate: get(periodDetails, 'startDate') })

    if (value) {
      setInvoiceDate(locked ? addDays(lockDateFormatted, 1) : createDateFromString(value))
    }
  }

  const handleChangeResetRentalFees = event => {
    setResetRentalFees(event.target.checked)
  }

  const initiateRentalFees = () => {
    if (isRentalFeeEnabled) {
      getAccountSettings({ accountId }, true).unwrap().catch(handleError)
      getRentalFeesLinkedToAccount({ accountId }).unwrap().catch(handleError)
    }
  }

  useEffect(() => {
    if (!isSelectedPeriodValidForLateFees) {
      setSelectedPeriod({ locked: false, invoiceDate: '', periodName: '', startDate: '' })
    }
  }, [isSelectedPeriodValidForLateFees])

  useEffect(() => {
    if (accountId) {
      getAccountBillingPeriods({ accountId })
        .unwrap()
        .catch(error => {
          handleError(error)
          onClose()
        })
      initiateRentalFees()
    }
  }, [accountId])

  return (
    <>
      {(isLoading || isCreateInvoiceLoading || isPreviewInvoiceLoading || isAccountSettingsLoading || isRentalFeesLoading) && <Loader />}
      <Dialog open={isOpen} maxWidth={isFirstStep ? 'sm' : 'lg'} fullWidth>
        <DialogTitle>
          <Typography variant="h4">Create invoice</Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 0, maxHeight: 'calc(100vh - 270px)' }}>
          <Box px={2} pb={2} sx={{ borderBottom: '1px dashed', borderColor: 'text.secondary' }}>
            <Stepper
              sx={{
                width: isFirstStep ? 'auto' : '500px',
                margin: isFirstStep ? 'auto' : '0 auto',
                '.MuiStepConnector-line': {
                  borderTopStyle: 'dashed',
                  borderColor: isFirstStep ? 'text.secondary' : 'primary.main',
                },
              }}
              activeStep={activeStep}
            >
              {STEPS.map((label, index) => {
                const labelProps = {}
                const sx = index === 1 ? { '.MuiStepLabel-labelContainer': { position: 'relative', top: 10 } } : {}
                labelProps.optional = (
                  <Typography whiteSpace="pre-line" variant="caption">
                    {label.optionalText}
                  </Typography>
                )

                return (
                  <Step key={label.stepName}>
                    <StepLabel sx={sx} {...labelProps}>
                      {label.stepName}
                    </StepLabel>
                  </Step>
                )
              })}
            </Stepper>
          </Box>

          <DialogContentText>
            <Box mt={2} px={3}>
              {isFirstStep && (
                <>
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={8}>
                    <FormControl>
                      <Typography variant="caption">{T.INVOICE_TYPE}</Typography>

                      <RadioGroup value={type} onChange={handleInvoiceTypeChange}>
                        <FormControlLabel
                          value={MANUAL}
                          control={<Radio size="small" />}
                          label={<Typography color="textPrimary">{T.MANUAL}</Typography>}
                        />
                        <FormControlLabel
                          value={RECURRING}
                          control={<Radio size="small" />}
                          label={<Typography color="textPrimary">{T.RECURRING}</Typography>}
                        />
                      </RadioGroup>
                    </FormControl>

                    {showInvoiceDatePicker && (
                      <HHDesktopDatePicker
                        label="Invoice date"
                        value={invoiceDate}
                        onChange={date => setInvoiceDate(date)}
                        TextFieldProps={{ deprecatedLabel: false }}
                        shouldDisableDate={date => checkIsLocked(formatDateToBEFormatDateFns(date))}
                      />
                    )}
                  </Box>

                  {showResetRentalFeesOption && (
                    <FormControlLabel
                      control={<Checkbox checked={resetRentalFees} onChange={handleChangeResetRentalFees} />}
                      label={<Typography color="textPrimary">{T.BILL_RENTAL_FEES_ON_INVOICE}</Typography>}
                    />
                  )}

                  {isRecurringInvoice && (
                    <HHTextField
                      sx={{
                        mt: 2,
                        '& .MuiSelect-select': {
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        },
                      }}
                      deprecatedLabel={false}
                      label={T.BILLING_PERIOD}
                      fullWidth
                      select
                      helperText={isLateFeeEnabled && includeLateFees && T.UNCHECK_LATE_FEES_TO_INCLUDE_PREVIOUS_PERIODS}
                      SelectProps={{ MenuProps: { disableScrollLock: true } }}
                      value={[selectedPeriodDate]}
                      onChange={handleBillingPeriodChange}
                    >
                      <SelectItem value="">{T.SELECT_PERIOD_PLACEHOLDER}</SelectItem>
                      {mappedBillingPeriods.map(({ invoiceDate: invDate, isLockedLateFeeRelated, labelLeft, labelRight }) => {
                        if (isLockedLateFeeRelated) {
                          return (
                            <BillingPeriodOptionTooltip isLocked={isLockedLateFeeRelated} invoiceDate={invDate}>
                              <SelectItem disabled={isLockedLateFeeRelated} value={invDate}>
                                <Typography fontWeight={600} noWrap>
                                  {labelLeft}
                                </Typography>
                                <Typography noWrap>{labelRight}</Typography>
                              </SelectItem>
                            </BillingPeriodOptionTooltip>
                          )
                        }
                        return (
                          <SelectItem value={invDate}>
                            <Typography fontWeight={600} noWrap>
                              {labelLeft}
                            </Typography>
                            <Typography noWrap>{labelRight}</Typography>
                          </SelectItem>
                        )
                      })}
                    </HHTextField>
                  )}
                  {isLateFeeEnabled && (
                    <FormControlLabel
                      control={<Checkbox size="small" checked={includeLateFees} onChange={handleIncludeLateFeesChange} />}
                      label={
                        <Typography noWrap color="textPrimary">
                          {T.INCLUDE_LATE_FEES}
                        </Typography>
                      }
                    />
                  )}
                </>
              )}

              {!isFirstStep && (
                <PreviewCharges
                  type={type}
                  selectedPeriod={selectedPeriod}
                  invoiceDate={finalDate}
                  isLoadingFirstBatch={isLoadingFirstBatch}
                  loadMoreRows={loadMoreRows}
                />
              )}
            </Box>
          </DialogContentText>
        </DialogContent>
        <HHDialogActions>
          {!isFirstStep && (
            <Button size="small" variant="outlined" onClick={handleBack} sx={{ mr: 1 }}>
              Go back
            </Button>
          )}

          <Box sx={{ flex: '1 1 auto' }} />

          <CancelButton onClick={handleReset} sx={{ mr: 1 }} />
          <Button sx={{ ml: 1 }} size="small" variant="contained" disabled={isSubmitBtnDisabled} onClick={handleNext}>
            {capitalize(nextButtonLabel)}
          </Button>
        </HHDialogActions>
      </Dialog>
    </>
  )
}

CreateInvoice.propTypes = {
  isOpen: PropTypes.bool,
  billingProfileDetails: PropTypes.object,
  callback: PropTypes.func,
  accountId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default CreateInvoice
